import { useState } from "react";
import { api } from "../app/api";
import { useAsync } from "../app/useAsync";
import { StatusChip } from "../components/StatusChip";

type Entity = "club" | "event" | "workout";
const LABEL: Record<Entity, string> = {
  club: "Ruck Clubs (sandlot_ruck_club)",
  event: "Events (sandlot_event)",
  workout: "Workouts (sandlot_workout)",
};

/** Phase 5A/5B — bulk metaobject migration progress + controls. */
export function ClubMigrationPage() {
  const [entity, setEntity] = useState<Entity>("club");
  const [reloadKey, setReloadKey] = useState(0);
  const readiness = useAsync(() => api.metaobjectReadiness(entity), [entity, reloadKey]);
  const [running, setRunning] = useState(false);
  const [log, setLog] = useState<string[]>([]);

  const append = (l: string) => setLog((x) => [...x, l]);

  async function approve() {
    await api.approveEntityBatch(entity);
    append(`✔ Batch approval recorded for ${entity}`);
    setReloadKey((k) => k + 1);
  }

  async function runAll() {
    setRunning(true);
    append(`▶ Running ${entity} batches…`);
    try {
      let cursor = "";
      for (let i = 0; i < 2000; i++) {
        const r = await api.runEntityBatch(entity, 25, cursor);
        if (!r.ok) { append(`✗ ${r.error}`); break; }
        cursor = r.nextCursor ?? cursor;
        append(`batch: +${r.completed} done, ${r.failed} failed → ${r.totalMigrated}/${r.total}`);
        if (r.done || (r.batchProcessed ?? 0) === 0) { append("✔ Complete."); break; }
      }
    } catch (e) {
      append(`✗ ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setRunning(false);
      setReloadKey((k) => k + 1);
    }
  }

  const d = readiness.data;
  const pct = d && d.total ? Math.round((d.alreadyMigrated / Math.max(d.total, 1)) * 100) : 0;

  return (
    <>
      <h1>Metaobject Migration</h1>
      <p className="page-sub">
        Bulk migration of Sandlot metaobjects. Idempotent and resumable — batches
        skip entities already in the id map.
      </p>

      <div className="card" style={{ marginBottom: "1.2rem" }}>
        <h3>Entity</h3>
        <select
          value={entity}
          onChange={(e) => { setEntity(e.target.value as Entity); setLog([]); }}
          style={{ padding: "0.5rem 0.7rem", background: "var(--surface)", border: "1px solid var(--hair)", borderRadius: 8, color: "var(--ink)" }}
        >
          <option value="club">{LABEL.club}</option>
          <option value="event">{LABEL.event}</option>
          <option value="workout">{LABEL.workout}</option>
        </select>
      </div>

      <div className="grid" style={{ marginBottom: "1.2rem" }}>
        <div className="card"><h3>Total</h3><div className="stat">{d?.total ?? "…"}</div></div>
        <div className="card"><h3>Migrated</h3><div className="stat">{d?.alreadyMigrated ?? "…"}</div><p className="muted">{pct}%</p></div>
        <div className="card"><h3>Remaining</h3><div className="stat">{d?.remaining ?? "…"}</div></div>
        <div className="card"><h3>Blocked</h3><div className="stat">{d?.blocked ?? "…"}</div><p className="muted">{d?.warnings ?? 0} with warnings</p></div>
      </div>

      <div className="card" style={{ marginBottom: "1.2rem" }}>
        <h3>Controls</h3>
        <p style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
          <StatusChip tone={d?.batchApproved ? "ok" : "warn"} label={`Batch approval: ${d?.batchApproved ? "approved" : "required"}`} />
          <StatusChip tone={d?.writesEnabled ? "ok" : "warn"} label={`Writes: ${d?.writesEnabled ? "enabled" : "disabled"}`} />
          <StatusChip tone={d && d.validationPassed === d.total ? "ok" : "warn"} label={`Validation: ${d?.validationPassed ?? "…"}/${d?.total ?? "…"}`} />
        </p>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button style={btn} onClick={approve} disabled={running}>Approve Batch</button>
          <button style={{ ...btn, background: "var(--accent)", color: "#12140f" }} onClick={runAll} disabled={running || !d?.writesEnabled || !d?.batchApproved}>
            {running ? "Running…" : "Run Migration"}
          </button>
        </div>
        {!d?.writesEnabled && (
          <p className="muted" style={{ marginTop: 10 }}>
            Writes are disabled. Enable MIGRATION_WRITES_ENABLED for the migration window to run.
          </p>
        )}
      </div>

      <div className="card">
        <h3>Activity</h3>
        {log.length === 0 ? <p className="muted">No activity yet.</p> : <pre style={pre}>{log.join("\n")}</pre>}
      </div>
    </>
  );
}

const btn: React.CSSProperties = {
  padding: "0.5rem 1rem",
  background: "var(--surface-2)",
  color: "var(--ink)",
  border: "1px solid var(--hair)",
  borderRadius: 8,
  fontWeight: 600,
  cursor: "pointer",
};
const pre: React.CSSProperties = {
  overflowX: "auto",
  fontSize: "0.78rem",
  background: "var(--surface-2)",
  padding: "0.9rem",
  borderRadius: 8,
  whiteSpace: "pre-wrap",
};
